import { Fragment, useState } from "react";

import { useT } from "../../../shared/i18n";
import { Badge, Button } from "../../../shared/ui";
import { formatDuration, formatTime } from "./logFormat";
import { TraceRecordDetails } from "./TraceRecordDetails";
import type { TraceRecord } from "./traceApi";

const ERROR_LEVELS = new Set(["error", "critical", "fatal"]);

function tokens(record: TraceRecord): string {
  if (record.input_tokens === null && record.output_tokens === null) return "-";
  return `${String(record.input_tokens ?? "-")} / ${String(record.output_tokens ?? "-")}`;
}

function http(record: TraceRecord): string {
  const parts = [record.http_method, record.http_route, record.http_status]
    .filter((part) => part !== null)
    .map(String);
  return parts.length > 0 ? parts.join(" ") : "-";
}

function model(record: TraceRecord): string {
  const parts = [record.provider, record.model].filter(
    (part): part is string => part !== null,
  );
  return parts.length > 0 ? parts.join(" / ") : "-";
}

/** One call as a chronological table; each row expands to the other fields. */
export function TraceTimeline({ records }: { records: TraceRecord[] }) {
  const t = useT();
  const [open, setOpen] = useState<ReadonlySet<number>>(new Set());
  const toggle = (index: number) => {
    const next = new Set(open);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setOpen(next);
  };
  const headers = [
    t("admin.logs.timeline.col.time"),
    t("admin.logs.timeline.col.service"),
    t("admin.logs.timeline.col.level"),
    t("admin.logs.timeline.col.event"),
    t("admin.logs.timeline.col.operation"),
    t("admin.logs.timeline.col.outcome"),
    t("admin.logs.timeline.col.code"),
    t("admin.logs.timeline.col.duration"),
    t("admin.logs.timeline.col.model"),
    t("admin.logs.timeline.col.tokens"),
    t("admin.logs.timeline.col.http"),
    "",
  ];
  if (records.length === 0) {
    return (
      <p className="text-body text-fg-muted">
        {t("admin.logs.timeline.empty")}
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl bg-raised shadow-card">
      <table className="w-full border-collapse text-callout">
        <caption className="sr-only">
          {t("admin.logs.timeline.caption")}
        </caption>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th
                key={`${header}-${String(index)}`}
                scope="col"
                className="bg-card-alt px-3 py-2.5 text-left text-caption font-semibold uppercase tracking-caps text-fg-muted"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((record, index) => (
            <Fragment key={`${record.time}-${String(index)}`}>
              <tr>
                <td className="px-3 py-2 whitespace-nowrap">
                  {formatTime(record.time)}
                </td>
                <td className="px-3 py-2">{record.service}</td>
                <td className="px-3 py-2">
                  <Badge
                    tone={ERROR_LEVELS.has(record.level) ? "error" : "neutral"}
                  >
                    {record.level}
                  </Badge>
                </td>
                <td className="px-3 py-2">{record.event}</td>
                <td className="px-3 py-2">{record.operation ?? "-"}</td>
                <td className="px-3 py-2">{record.outcome ?? "-"}</td>
                <td className="px-3 py-2">{record.code ?? "-"}</td>
                <td className="px-3 py-2">
                  {formatDuration(record.duration_ms)}
                </td>
                <td className="px-3 py-2">{model(record)}</td>
                <td className="px-3 py-2">{tokens(record)}</td>
                <td className="px-3 py-2">{http(record)}</td>
                <td className="px-3 py-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-expanded={open.has(index)}
                    onClick={() => {
                      toggle(index);
                    }}
                  >
                    {open.has(index)
                      ? t("admin.logs.timeline.collapse")
                      : t("admin.logs.timeline.expand")}
                  </Button>
                </td>
              </tr>
              {open.has(index) && (
                <tr>
                  <td
                    colSpan={headers.length}
                    className="bg-card-alt px-3 py-3"
                  >
                    <TraceRecordDetails record={record} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
